import {
  IonButton,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
} from '@ionic/react';
import { checkmark } from 'ionicons/icons';
import { cloneDeep, isEmpty } from 'lodash';
import { ReactElement, ReactNode, useCallback, useMemo, useRef } from 'react';
import { MdCompress, MdExpand } from 'react-icons/md';
import {
  TrackCobotWelderSelectionType,
  trackCobotWelderSectionCollapseAll,
  trackCobotWelderSectionExpandAll,
  trackCobotWelderSectionJumpTo,
  trackCobotWelderSelectAllItems,
  trackCobotWelderSelectItems,
  trackCobotWelderUnselectAllItems,
  trackCobotWelderUnselectItems,
} from '../../../../../../config/analytics';
import { getLocalizedText } from '../../../../../../i18n/i18n.utils';
import { FlowRef } from '../../../../shared/component/flow/flow-ref';
import { FlowItemIcon } from '../../../../shared/component/flow-item/view/flow-item-icon';
import { useFlowServices } from '../../../../shared/hook/flow-service.hooks';
import {
  Flow,
  FlowItem,
  FlowSelectedItemStats,
} from '../../../../shared/model/flow.types';
import { getParameterValue } from '../../../../shared/util/flow-parameter.util';
import { scrollToFlowItem } from '../../../../shared/util/flow.util';
import { BeaconWeldingData } from '../../model/beacon-welding-data';
import { isWeldType } from '../../util/beacon-welding.util';
import {
  getFlowItemIcon,
  getWaitForType,
} from '../flow-item/welding-flow-item';
import './welding-actions.css';

export interface WeldingEditAction {
  label: string | ReactElement;
  isSelected?: boolean;
  disabled?: boolean;
  onSelect: () => void;
  icon?: ReactNode;
}

interface WeldingEditActionsProps {
  flow: Flow;
  flowRef: FlowRef;
  dismiss?: () => void;
}

const WeldingEditActions = (props: WeldingEditActionsProps): ReactElement => {
  const { flow, flowRef, dismiss } = props;

  const { selectedItemDetails, setSelectedItemIds, toggleCollapseAll } =
    flowRef;

  const { selectedItemIds, selectedFlowItemInfo: selectedItemInfo } =
    selectedItemDetails;

  const toggleWeldType = useRef<
    (weldType: BeaconWeldingData.WeldTypeEnum) => void
  >(() => null);

  const getOtherIconsRef = useRef<() => ReactNode[]>(() => []);
  const isOtherTypeSelected = isTypeSelected(selectedItemInfo?.other);

  getOtherIconsRef.current = () =>
    getFirstNOtherTypes(flow, 2, isOtherTypeSelected).map((el, index) => {
      return (
        <div
          key={index}
          style={{
            position: index === 0 ? 'unset' : 'absolute',
            transform: `translateX(${8 * index}px)`,
            zIndex: -index,
          }}
        >
          {el}
        </div>
      );
    });

  toggleWeldType.current = (weldType: BeaconWeldingData.WeldTypeEnum): void => {
    let stats: FlowSelectedItemStats | undefined;
    let type: TrackCobotWelderSelectionType = 'other';
    switch (weldType) {
      case BeaconWeldingData.WeldTypeEnum.LINEAR:
        stats = selectedItemInfo?.linear;
        type = 'linear';
        break;
      case BeaconWeldingData.WeldTypeEnum.LINEAR_STITCH:
        stats = selectedItemInfo?.linearStitch;
        type = 'stitch';
        break;
      case BeaconWeldingData.WeldTypeEnum.RADIAL:
        stats = selectedItemInfo?.radial;
        type = 'radial';
        break;
      case BeaconWeldingData.WeldTypeEnum.TACK:
        stats = selectedItemInfo?.tack;
        type = 'tack';
        break;
      case BeaconWeldingData.WeldTypeEnum.ROTARY:
        stats = selectedItemInfo?.rotary;
        type = 'rotary';
        break;
    }

    if (stats && stats.count === stats.selected) {
      const nextSelectedItemIds = unselectWeldTypes(
        flow,
        selectedItemIds,
        weldType
      );
      const prevSelected = selectedItemIds.length;
      const totalSelected = nextSelectedItemIds.length;
      setSelectedItemIds(unselectWeldTypes(flow, selectedItemIds, weldType));
      trackCobotWelderUnselectItems({
        method: 'actionBar',
        flowLength: flow.items.length,
        count: prevSelected - totalSelected,
        totalSelected,
        type,
      });
    } else {
      const nextSelectedItemIds = selectWeldTypes(
        flow,
        selectedItemIds,
        weldType
      );
      const prevSelected = selectedItemIds.length;
      const totalSelected = nextSelectedItemIds.length;
      setSelectedItemIds(nextSelectedItemIds);
      trackCobotWelderSelectItems({
        method: 'actionBar',
        flowLength: flow.items.length,
        count: totalSelected - prevSelected,
        totalSelected,
        type,
      });
    }
  };

  const toggleOtherTypes = useRef<() => void>(() => null);

  toggleOtherTypes.current = (): void => {
    if (
      selectedItemInfo &&
      selectedItemInfo.other.count === selectedItemInfo.other.selected
    ) {
      const nextSelectedItemIds = unselectOtherTypes(flow, selectedItemIds);
      const prevSelected = selectedItemIds.length;
      const totalSelected = nextSelectedItemIds.length;
      setSelectedItemIds(nextSelectedItemIds);
      trackCobotWelderUnselectItems({
        method: 'actionBar',
        flowLength: flow.items.length,
        count: prevSelected - totalSelected,
        totalSelected,
        type: 'other',
      });
    } else {
      const nextSelectedItemIds = selectOtherTypes(flow, selectedItemIds);
      const prevSelected = selectedItemIds.length;
      const totalSelected = nextSelectedItemIds.length;
      setSelectedItemIds(nextSelectedItemIds);
      trackCobotWelderSelectItems({
        method: 'actionBar',
        flowLength: flow.items.length,
        count: totalSelected - prevSelected,
        totalSelected,
        type: 'other',
      });
    }
  };

  const sections = getSections(flow);
  const hasSections = useMemo(
    () => sections.find((section) => section.id !== undefined) !== undefined,
    [sections]
  );

  const getActions: () => WeldingEditAction[] = useCallback(() => {
    return [
      {
        label: `${getLocalizedText('welding:select_tacks')} (${
          selectedItemInfo?.tack.count ?? 0
        })`,
        icon: (
          <FlowItemIcon
            className="edit-action-icon"
            icon={getFlowItemIcon(
              BeaconWeldingData.FlowItemRef.WELD,
              BeaconWeldingData.WeldTypeEnum.TACK
            )}
            isItemSelected={isTypeSelected(selectedItemInfo?.tack)}
          />
        ),
        disabled: isTypeDisabled(selectedItemInfo?.tack),
        onSelect: () => {
          toggleWeldType.current(BeaconWeldingData.WeldTypeEnum.TACK);
        },
      },
      {
        label: `${getLocalizedText('welding:select_linear')} (${
          selectedItemInfo?.linear.count ?? 0
        })`,
        icon: (
          <FlowItemIcon
            className="edit-action-icon"
            icon={getFlowItemIcon(
              BeaconWeldingData.FlowItemRef.WELD,
              BeaconWeldingData.WeldTypeEnum.LINEAR
            )}
            isItemSelected={isTypeSelected(selectedItemInfo?.linear)}
          />
        ),
        disabled: isTypeDisabled(selectedItemInfo?.linear),
        onSelect: () => {
          toggleWeldType.current(BeaconWeldingData.WeldTypeEnum.LINEAR);
        },
      },
      {
        label: `${getLocalizedText('welding:select_radial')} (${
          selectedItemInfo?.radial.count ?? 0
        })`,
        icon: (
          <FlowItemIcon
            className="edit-action-icon"
            icon={getFlowItemIcon(
              BeaconWeldingData.FlowItemRef.WELD,
              BeaconWeldingData.WeldTypeEnum.RADIAL
            )}
            isItemSelected={isTypeSelected(selectedItemInfo?.radial)}
          />
        ),
        disabled: isTypeDisabled(selectedItemInfo?.radial),
        onSelect: () => {
          toggleWeldType.current(BeaconWeldingData.WeldTypeEnum.RADIAL);
        },
      },
      {
        label: `${getLocalizedText('welding:select_linear_stitch')} (${
          selectedItemInfo?.linearStitch.count ?? 0
        })`,
        icon: (
          <FlowItemIcon
            className="edit-action-icon"
            icon={getFlowItemIcon(
              BeaconWeldingData.FlowItemRef.WELD,
              BeaconWeldingData.WeldTypeEnum.LINEAR_STITCH
            )}
            isItemSelected={isTypeSelected(selectedItemInfo?.linearStitch)}
          />
        ),
        disabled: isTypeDisabled(selectedItemInfo?.linearStitch),
        onSelect: () => {
          toggleWeldType.current(BeaconWeldingData.WeldTypeEnum.LINEAR_STITCH);
        },
      },
      {
        label: `${getLocalizedText('welding:select_rotary')} (${
          selectedItemInfo?.rotary.count ?? 0
        })`,
        icon: (
          <FlowItemIcon
            className="edit-action-icon"
            icon={getFlowItemIcon(
              BeaconWeldingData.FlowItemRef.WELD,
              BeaconWeldingData.WeldTypeEnum.ROTARY
            )}
            isItemSelected={isTypeSelected(selectedItemInfo?.rotary)}
          />
        ),
        disabled: isTypeDisabled(selectedItemInfo?.rotary),
        onSelect: () => {
          toggleWeldType.current(BeaconWeldingData.WeldTypeEnum.ROTARY);
        },
      },
      {
        label: `${getLocalizedText('welding:select_other')} (${
          selectedItemInfo?.other.count ?? 0
        })`,
        disabled: isTypeDisabled(selectedItemInfo?.other),
        icon: (
          <div style={{ display: 'flex' }}>{getOtherIconsRef.current()}</div>
        ),
        onSelect: () => {
          toggleOtherTypes.current();
        },
      },
    ];
  }, [selectedItemInfo]);

  const actions = getActions();

  return (
    <div className="edit-actions-container">
      <div className="edit-actions-button-container">
        <IonButton
          slot="start"
          fill="clear"
          onClick={() => {
            setSelectedItemIds(selectAll(flow));
            trackCobotWelderSelectAllItems(flow.items.length);
            if (dismiss) {
              dismiss();
            }
          }}
        >
          <IonLabel color="primary">
            {getLocalizedText('welding:select_all')}
          </IonLabel>
        </IonButton>
        <IonButton
          slot="end"
          fill="clear"
          onClick={() => {
            setSelectedItemIds(unselectAll());
            trackCobotWelderUnselectAllItems(flow.items.length);
            if (dismiss) {
              dismiss();
            }
          }}
        >
          <IonLabel color="primary">
            {getLocalizedText('welding:unselect_all')}
          </IonLabel>
        </IonButton>
      </div>
      <IonList>
        <IonListHeader>
          {getLocalizedText('welding:customize_select')}
        </IonListHeader>
        {actions.map((a, index) => {
          const lines = index === actions.length - 1 ? 'none' : 'full';
          return (
            <IonItem
              className={a.disabled ? 'flow-item-inactive' : ''}
              lines={lines}
              key={index}
              href="#"
              detail={false}
              disabled={a.disabled}
              onClick={(e) => {
                e.preventDefault();
                a.onSelect();
              }}
            >
              {a.icon ?? (
                <IonIcon
                  slot="start"
                  size="small"
                  icon={checkmark}
                  color={a.isSelected ? 'primary' : 'light'}
                />
              )}
              <IonLabel>{a.label}</IonLabel>
            </IonItem>
          );
        })}
        <SectionActions
          hasSections={hasSections}
          sections={sections}
          toggleCollapseAll={toggleCollapseAll}
          dismiss={dismiss}
        />
      </IonList>
      {hasSections && <div className="popover-fade" />}
    </div>
  );
};

interface SectionActionProps {
  hasSections: boolean;
  sections: FlowItem[] | undefined;
  toggleCollapseAll: (collapsed: boolean) => void;
  dismiss?: () => void;
}

const SectionActions = (props: SectionActionProps): ReactElement | null => {
  const { hasSections, sections, toggleCollapseAll, dismiss } = props;

  const { flowItemService } = useFlowServices();
  const sectionTexts = flowItemService.buildFlowItemTextArr(sections ?? []);

  const jumpToSectionActions: ReactElement[] =
    sections?.reduce((actions, section, idx) => {
      if (section.id === undefined) {
        return actions;
      }

      const color = getParameterValue(
        section,
        BeaconWeldingData.FlowItemParameterRef.SECTION_COLOR
      );
      const borderLeftColor = color ? `${color}` : `transparent`;

      const sectionLabel =
        sectionTexts[idx]?.label ??
        sectionTexts[idx]?.name ??
        getLocalizedText('welding:section');

      const lines = idx === sections.length - 1 ? 'none' : 'full';
      actions.push(
        <IonItem
          lines={lines}
          key={idx}
          href="#"
          detail={false}
          onClick={(e) => {
            e.preventDefault();
            if (section.id !== undefined) {
              scrollToFlowItem(section.id, { skipHeaderPadding: true });
              trackCobotWelderSectionJumpTo();
              if (dismiss) {
                dismiss();
              }
            }
          }}
        >
          <div
            // slot="start"
            className="edit-action-section-indicator"
            style={{
              borderLeftColor,
            }}
          />
          <IonLabel>{sectionLabel}</IonLabel>
        </IonItem>
      );
      return actions;
    }, [] as ReactElement[]) ?? [];

  if (hasSections && sections) {
    return (
      <>
        <div style={{ height: '20px' }} />
        <IonListHeader>
          {getLocalizedText('welding:section_shortcuts')}
        </IonListHeader>
        <IonItem
          className={'flow-item-inactive'}
          lines={'full'}
          href="#"
          detail={false}
          onClick={(e) => {
            e.preventDefault();
            toggleCollapseAll(false);
            trackCobotWelderSectionExpandAll();
          }}
        >
          <MdExpand />
          <IonLabel>{getLocalizedText('welding:section_expand_all')}</IonLabel>
        </IonItem>
        <IonItem
          className={'flow-item-inactive'}
          lines={'none'}
          href="#"
          detail={false}
          onClick={(e) => {
            e.preventDefault();
            toggleCollapseAll(true);
            trackCobotWelderSectionCollapseAll();
          }}
        >
          <MdCompress />
          <IonLabel>
            {getLocalizedText('welding:section_collapse_all')}
          </IonLabel>
        </IonItem>
        <div style={{ height: '10px' }} />
        <IonListHeader>
          {getLocalizedText('welding:section_jump_to')}
        </IonListHeader>
        {jumpToSectionActions}
        <div style={{ height: '10px' }} />
      </>
    );
  } else {
    return null;
  }
};

const isTypeSelected = (stats?: FlowSelectedItemStats): boolean => {
  if (stats && stats.count == stats.selected && stats.count > 0) {
    return true;
  }
  return false;
};

const isTypeDisabled = (stats?: FlowSelectedItemStats): boolean => {
  if (stats && stats.count === 0) {
    return true;
  }
  return false;
};

const selectWeldTypes = (
  flow: Flow,
  selectedItemIds: string[],
  weldType: BeaconWeldingData.WeldTypeEnum
): string[] => {
  const selected = cloneDeep(selectedItemIds);
  flow.items.map((item) => {
    if (
      isWeldType(item, weldType) &&
      item.id &&
      !selectedItemIds.includes(item.id)
    ) {
      selected.push(item.id);
    }
  });
  return selected;
};

const unselectWeldTypes = (
  flow: Flow,
  selectedItemIds: string[],
  weldType: BeaconWeldingData.WeldTypeEnum
): string[] => {
  let selected = cloneDeep(selectedItemIds);
  flow.items.map((item) => {
    if (isWeldType(item, weldType)) {
      selected = selected.filter((i) => i !== item.id);
    }
  });
  return selected;
};

const selectOtherTypes = (flow: Flow, selectedItemIds: string[]): string[] => {
  const selected = cloneDeep(selectedItemIds);
  flow.items.map((item) => {
    if (
      item.ref !== BeaconWeldingData.FlowItemRef.WELD &&
      item.id &&
      !selectedItemIds.includes(item.id)
    ) {
      selected.push(item.id);
    }
  });
  return selected;
};

const unselectOtherTypes = (
  flow: Flow,
  selectedItemIds: string[]
): string[] => {
  let selected = cloneDeep(selectedItemIds);
  flow.items.map((item) => {
    if (
      item.ref !== BeaconWeldingData.FlowItemRef.WELD &&
      item.id &&
      selectedItemIds.includes(item.id)
    ) {
      selected = selected.filter((i) => i !== item.id);
    }
  });
  return selected;
};

const getSections = (flow: Flow, saved?: boolean) => {
  return flow.items.filter((item) => {
    return (
      item.ref === BeaconWeldingData.FlowItemRef.SECTION &&
      (saved ? item.id !== undefined : true)
    );
  });
};

const selectAll = (flow: Flow): string[] => {
  return flow.items.map((item) => item.id as string);
};

const unselectAll = (): string[] => {
  return [];
};

const getFirstNOtherTypes = (
  flow: Flow,
  num = 3,
  isSelected?: boolean
): ReactNode[] => {
  const icons: ReactNode[] = [];
  const refsCollected: string[] = [];

  const defaultIcon = (
    <FlowItemIcon
      className="edit-action-icon"
      icon={getFlowItemIcon(BeaconWeldingData.FlowItemRef.MOVE)}
      isItemSelected={isSelected}
    />
  );

  flow.items.map((item) => {
    if (
      item.ref !== BeaconWeldingData.FlowItemRef.WELD &&
      item.ref !== BeaconWeldingData.FlowItemRef.SECTION &&
      icons.length < num &&
      !refsCollected.includes(item.ref)
    ) {
      refsCollected.push(item.ref);
      const waitForType = getWaitForType(item);
      icons.push(
        <FlowItemIcon
          className="edit-action-icon"
          icon={getFlowItemIcon(item.ref, undefined, waitForType?.value)}
          isItemSelected={isSelected}
        />
      );
    }
  });

  return isEmpty(icons) ? [defaultIcon] : icons;
};

export { WeldingEditActions };
